import React, { useState, useEffect } from "react";
import {
  Box, Typography, TextField, Button, Autocomplete, CircularProgress,
  ToggleButtonGroup, ToggleButton, RadioGroup, FormControlLabel, Radio,
  FormLabel, FormControl, Collapse,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useGetDocRepMutation, useSubmitLycoredMutation } from "state/api";
import CameraUpload from "components/CameraUpload";
import { toast } from "react-toastify";
import lycoredLogo from "../../assets/Lycored-M.png";

const LycoRedMLogo = () => (
  <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
    <Box
      component="img"
      src={lycoredLogo}
      alt="LycoRed M"
      sx={{ maxHeight: 110, width: "auto", objectFit: "contain" }}
    />
  </Box>
);

/* ── Yes/No toggle helper ── */
const YesNoToggle = ({ value, onChange, required }) => (
  <ToggleButtonGroup
    exclusive value={value}
    onChange={(_, v) => { if (v !== null) onChange(v); }}
    size="small"
    sx={{ mb: 0 }}
  >
    <ToggleButton
      value="yes"
      sx={{
        px: 4, fontWeight: 700,
        "&.Mui-selected": { bgcolor: "#2E7D32", color: "white", "&:hover": { bgcolor: "#1B5E20" } },
      }}
    >
      Yes
    </ToggleButton>
    <ToggleButton
      value="no"
      sx={{
        px: 4, fontWeight: 700,
        "&.Mui-selected": { bgcolor: "#C62828", color: "white", "&:hover": { bgcolor: "#7B0000" } },
      }}
    >
      No
    </ToggleButton>
  </ToggleButtonGroup>
);

const LycoredTracker = () => {
  const { user } = useSelector((s) => s.auth);
  const [getDocRep] = useGetDocRepMutation();
  const [submitLycored, { isLoading }] = useSubmitLycoredMutation();

  const [doctors, setDoctors] = useState([]);
  const [doctor, setDoctor] = useState(null);
  const [practicingInIvf, setPracticingInIvf] = useState("no");
  const [ivfCenterName, setIvfCenterName] = useState("");
  const [monthlyPotential, setMonthlyPotential] = useState("<50");
  const [lycoredMAvailable, setLycoredMAvailable] = useState("no");
  const [stripsAvailable, setStripsAvailable] = useState("");
  const [bonanzaGiven, setBonanzaGiven] = useState("no");
  const [prescription, setPrescription] = useState(null);

  useEffect(() => {
    if (user?.role) {
      getDocRep({ MgCd: user.role, HqMstId: user.HqMstId })
        .unwrap()
        .then((res) => setDoctors(res.data || []))
        .catch(console.error);
    }
  }, [user, getDocRep]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!doctor) { toast.error("Please select a doctor"); return; }

    const fd = new FormData();
    fd.append("EmpNo", user.id);
    fd.append("MgCd", user.role);
    fd.append("doctorName", doctor.DoctName);
    fd.append("doctCd", doctor.DoctCd || "");
    fd.append("practicingInIvf", String(practicingInIvf === "yes"));
    fd.append("ivfCenterName", practicingInIvf === "yes" ? ivfCenterName : "");
    fd.append("monthlyPotential", monthlyPotential);
    fd.append("lycoredMAvailable", String(lycoredMAvailable === "yes"));
    fd.append("stripsAvailable", stripsAvailable || "0");
    fd.append("bonanzaGiven", String(bonanzaGiven === "yes"));
    if (prescription) fd.append("prescription", prescription);

    try {
      await submitLycored(fd).unwrap();
      toast.success("Lycored M tracker submitted!");
      setDoctor(null); setPracticingInIvf("no"); setIvfCenterName("");
      setMonthlyPotential("<50"); setLycoredMAvailable("no");
      setStripsAvailable(""); setBonanzaGiven("no"); setPrescription(null);
    } catch (err) {
      toast.error(err?.data?.message || "Submit failed");
    }
  };

  /* row: label on left, toggle on right */
  const FieldRow = ({ label, children }) => (
    <Box
      sx={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexWrap: "wrap", gap: 1, mb: 2,
        p: 1.5, border: "1px dashed #ddd", borderRadius: 2,
      }}
    >
      <Typography variant="body1" fontWeight={500} sx={{ color: "#333" }}>{label}</Typography>
      {children}
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 600, mx: "auto" }}>
      <LycoRedMLogo />

      <Typography
        variant="subtitle1" fontWeight={700} textAlign="center"
        sx={{ mb: 3, color: "#7B0000", textDecoration: "underline" }}
      >
        Top 10 Most Potential Doctors for Lycored M
      </Typography>

      <form onSubmit={handleSubmit}>
        {/* *Dr. Name */}
        <Autocomplete
          options={doctors}
          getOptionLabel={(opt) =>
            `${opt.DoctName} (${opt.DoctCd || "N/A"}) - ${opt.DocCategory || ""}`
          }
          value={doctor}
          onChange={(_, v) => setDoctor(v)}
          renderInput={(params) => (
            <TextField {...params} label="*Dr. Name" sx={{ mb: 2 }} required />
          )}
          isOptionEqualToValue={(opt, val) => opt.DoctCd === val.DoctCd}
        />

        {/* *Practicing in IVF center */}
        <FieldRow label="*Practicing in IVF center">
          <YesNoToggle value={practicingInIvf} onChange={setPracticingInIvf} />
        </FieldRow>

        {/* *If yes – IVF center name */}
        <Collapse in={practicingInIvf === "yes"}>
          <TextField
            fullWidth label="*If yes - Name of IVF center"
            value={ivfCenterName} onChange={(e) => setIvfCenterName(e.target.value)}
            sx={{ mb: 2 }}
            required={practicingInIvf === "yes"}
          />
        </Collapse>

        {/* *Total Monthly Potential */}
        <Box sx={{ mb: 2, p: 1.5, border: "1px dashed #ddd", borderRadius: 2 }}>
          <FormControl component="fieldset">
            <FormLabel
              sx={{ fontWeight: 700, color: "#333", mb: 0.5, fontSize: "0.95rem",
                "&.Mui-focused": { color: "#333" } }}
            >
              *Total Monthly Potential (Strips)
            </FormLabel>
            <RadioGroup
              row value={monthlyPotential}
              onChange={(e) => setMonthlyPotential(e.target.value)}
            >
              {["<50", "50-100", "100-200", "200+"].map((v) => (
                <FormControlLabel
                  key={v} value={v} control={<Radio size="small" sx={{ color: "#B71C1C", "&.Mui-checked": { color: "#B71C1C" } }} />}
                  label={<Typography variant="body2" fontWeight={500}>{v}</Typography>}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Box>

        {/* *Is Lycored M available */}
        <FieldRow label="*Is Lycored M available there">
          <YesNoToggle value={lycoredMAvailable} onChange={setLycoredMAvailable} />
        </FieldRow>

        {/* *Prescription/Invoice – if yes */}
        <Collapse in={lycoredMAvailable === "yes"}>
          <CameraUpload
            label="*Prescription / Invoice copy to upload"
            onFileSelect={setPrescription}
            accept="image/*,.pdf"
          />
        </Collapse>

        {/* *How many strips available */}
        <TextField
          fullWidth type="number"
          label="*How many strips of Lycored M available"
          value={stripsAvailable} onChange={(e) => setStripsAvailable(e.target.value)}
          inputProps={{ min: 0 }} sx={{ mb: 2 }} required
        />

        {/* *Bonanza given */}
        <FieldRow label="*Is any Bonanza given to Doctor">
          <YesNoToggle value={bonanzaGiven} onChange={setBonanzaGiven} />
        </FieldRow>

        <Button
          fullWidth variant="contained" type="submit" size="large"
          disabled={isLoading}
          sx={{ mt: 2, py: 1.5, bgcolor: "#B71C1C", "&:hover": { bgcolor: "#7B0000" } }}
        >
          {isLoading ? <CircularProgress size={24} color="inherit" /> : "Submit"}
        </Button>
      </form>

      <Typography
        variant="body2"
        sx={{ mt: 3, textAlign: "center", color: "text.secondary" }}
      >
        <Box component="span" sx={{ color: "#C41E3A", fontWeight: 700 }}>Note: </Box>
        Sections marked with{" "}
        <Box component="span" sx={{ color: "#C41E3A", fontWeight: 700 }}>*</Box>
        {" "}are mandatory for the submission
      </Typography>
    </Box>
  );
};

export default LycoredTracker;
